import getVideoEmbedInfo from './get-video-embed-info'
import { CreationToolVideoInfo } from '../types'
import { getUrlMimeType } from './get-source-info'

/**
 * Vimeo's privacy block, as gce-api passes it through from the Vimeo API
 * (giphy-services/src/python/gce-api/app.py `normalize_vimeo_response`). Only
 * the two settings we act on are modelled; everything else Vimeo sends is
 * ignored. YouTube lookups never carry a privacy block at all.
 */
type VideoPrivacy = {
    /** 'anybody' when the video is publicly watchable. */
    view?: string
    /** Whether the owner lets Vimeo hand out the source file. */
    download?: boolean
}

/**
 * Whether Vimeo positively says this video is *not* watchable by anybody.
 *
 * Deliberately conservative: a missing `view` means "we don't know", not
 * "private". Accusing a public video of being private is the exact bug this
 * discrimination exists to fix, so an unrecognised privacy block has to fall
 * through to the download check rather than short-circuit it here.
 */
const isViewRestricted = (privacy?: VideoPrivacy): boolean =>
    !!privacy && typeof privacy.view === 'string' && privacy.view !== 'anybody'

/**
 * Whether Vimeo says downloads are switched off for this video.
 *
 * A public, freely embeddable video can still have downloads off, and that
 * alone is enough to stop us making a GIF — we need the source file, not an
 * embed. This used to be folded into `isPrivate`, which made the UI tell
 * people a video they could plainly watch was "private".
 */
const areDownloadsDisabled = (privacy?: VideoPrivacy): boolean => !!privacy && !privacy.download

const getVideoInfo = (videoUrl: string, withCaption: boolean, quality = 'hd'): Promise<CreationToolVideoInfo> =>
    new Promise(async (resolve, reject) => {
        const mimeType = getUrlMimeType(videoUrl)

        // youtube/vimeo
        if (mimeType === 'youtube' || mimeType === 'vimeo') {
            try {
                const {
                    duration,
                    privacy,
                    title,
                    width,
                    height,
                    has_captions = false,
                    languages,
                } = await getVideoEmbedInfo(
                    videoUrl, // .replace(/(^\w+:|^)\/\//, '')
                    withCaption,
                    quality
                )
                resolve({
                    duration,
                    width,
                    height,
                    title,
                    isPrivate: isViewRestricted(privacy),
                    downloadsDisabled: areDownloadsDisabled(privacy),
                    hasCaptions: has_captions,
                    languages,
                })
            } catch (err) {
                reject(err)
            }

            // Without this the youtube/vimeo branch falls through and ALSO
            // creates a <video> pointed at the watch-page URL, which can never
            // load. That is a wasted request on every embed validation, and a
            // second settle path racing the fetch above.
            return
        }

        const video = document.createElement('video')
        video.onloadedmetadata = () => {
            resolve({
                duration: video.duration,
                width: video.videoWidth,
                height: video.videoHeight,
            })
        }
        video.onerror = reject
        video.src = videoUrl
    })

export default getVideoInfo
